import { useFieldArray, type FieldErrors, type Control } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';

import { ControlledTextField, SimpleButton, ErrorMessage } from '@/src/ui';
import { ControlledDynamicTextEditor, LETTERS, type QtiDragAndDropQuestionDTO } from '@/src/lib';
import { type TextEntryModalPartialProps } from '../../_shared';

interface DDChoiceEditorProps {
    question: QtiDragAndDropQuestionDTO;
    setModalProps: (props: TextEntryModalPartialProps) => void;
    control: Control<QtiDragAndDropQuestionDTO>;
    errors: FieldErrors<QtiDragAndDropQuestionDTO>;
}

export default function DDChoiceEditor({
    question,
    setModalProps,
    control,
    errors,
}: DDChoiceEditorProps) {
    const minChoices = 1;
    const maxChoices = 26;

    const { fields, append, remove } = useFieldArray({
        control,
        name: 'choices',
    });

    const handleAdd = () => {
        const nextLetter = suggestNextLetter(question.choices.map((c) => c.label ?? ''));
        append({
            id: uuidv4(),
            label: nextLetter,
            value: nextLetter,
            value_es: nextLetter,
            maxPlacements: 1,
        });
    };

    return (
        <>
            {fields.length === 0 && (
                <div className="text-sm text-gray-500">
                    No choices yet. Click <b>Add Choice</b> to begin.
                </div>
            )}

            <div style={{ overflowY: 'auto', maxHeight: '50vh' }}>
                {fields.map((field, index) => (
                    <div key={field.id} className="flex items-start gap-3">
                        <div className="flex flex-col gap-2">
                            <div className="flex gap-3 items-center">
                                <ControlledTextField
                                    name={`choices.${index}.label`}
                                    control={control}
                                    label="Label"
                                    placeholder="Visible label"
                                    error={errors.choices?.[index]?.label}
                                />
                                <div>
                                    {question.meta.isSpanishAllowed && (
                                        <div
                                            onClick={() =>
                                                setModalProps({
                                                    initialData: question.choices[index].value_es,
                                                    fieldName: `choices.${index}.value_es`,
                                                    friendlyName: 'Prompt (Spanish)',
                                                    textMode: 'rich',
                                                    error: errors?.choices?.[index]?.value_es
                                                        ?.message,
                                                })
                                            }
                                            style={{ cursor: 'pointer' }}
                                        >
                                            Edit Spanish
                                        </div>
                                    )}
                                    <ErrorMessage error={errors?.choices?.[index]?.value_es} />
                                    <ControlledDynamicTextEditor
                                        name={`choices.${index}.value`}
                                        label="Display value"
                                        control={control}
                                        initialValue={field.value}
                                        simpleMode
                                        style={{ minWidth: '250px' }}
                                        error={errors.choices?.[index]?.value}
                                    />
                                </div>
                                <ControlledTextField
                                    name={`choices.${index}.maxPlacements`}
                                    control={control}
                                    label="Max placements"
                                    type="number"
                                    error={errors.choices?.[index]?.maxPlacements}
                                />
                            </div>

                            <ErrorMessage error={errors.choices?.[index] as any} />
                        </div>

                        <div className="flex gap-1 pt-1">
                            <SimpleButton
                                type="button"
                                color="error"
                                variant="outlined"
                                disabled={fields.length <= minChoices}
                                onClick={() => remove(index)}
                            >
                                Remove
                            </SimpleButton>
                        </div>
                    </div>
                ))}
            </div>

            {/* Group-level errors */}
            <ErrorMessage error={errors.choices?.root} />
            <ErrorMessage error={errors.choices} />

            <SimpleButton
                type="button"
                color="primary"
                onClick={handleAdd}
                className="bg-blue-500 text-white px-4 py-2 rounded"
                disabled={fields.length >= maxChoices}
            >
                Add Choice
            </SimpleButton>
        </>
    );
}

function suggestNextLetter(values: string[]): string {
    const used = new Set(values.map((v) => (v ?? '').toUpperCase()));
    for (let index = 0; index <= LETTERS.length; index++) {
        const ch = LETTERS[index];
        if (!used.has(ch)) return ch;
    }
    return `C_${values.length + 1}`;
}
